/**
 * Error reporter - sends client-side errors to the API error reporting system.
 * All calls are fire-and-forget and will never throw.
 */

// Fall back to the real API host, matching every other client here. An empty fallback made this
// POST to a RELATIVE /api/errors/report — i.e. the Next server, which has no such route — so a
// missing env var silently swallowed every error report instead of reporting it.
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';
const ERROR_REPORT_ENDPOINT = `${API_URL}/api/errors/report`;

// Simple dedup: track recent fingerprints to avoid flooding
const recentErrors = new Set<string>();
const MAX_RECENT = 50;

function fingerprint(message: string, stack?: string): string {
  return `${message}|${(stack || '').substring(0, 100)}`;
}

function cleanup() {
  if (recentErrors.size > MAX_RECENT) {
    recentErrors.clear();
  }
}

/**
 * A stale tab after a deploy requests a chunk from the previous build, which no longer exists.
 * Turbopack throws a plain Error('Failed to load chunk …'), webpack a ChunkLoadError, and browsers
 * their own dynamic-import messages, so match on name OR message.
 */
export function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: unknown; message?: unknown } | null;
  const message =
    typeof error === 'string' ? error : typeof err?.message === 'string' ? err.message : '';
  return (
    err?.name === 'ChunkLoadError' ||
    /Failed to load chunk|Loading chunk [\w-]+ failed|Loading CSS chunk|Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
      message
    )
  );
}

const CHUNK_RELOAD_KEY_PREFIX = 'ug_chunk_reload:';
const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

/**
 * Hard-reload ONCE to pick up the new build after a chunk-load failure. Guarded per path in
 * sessionStorage (at most one auto-reload per path per 5 minutes) so a chunk that is permanently
 * blocked (ad-blocker, offline) can never cause a reload loop. Returns false — the caller should
 * report as usual — when a reload was already tried or storage is unavailable.
 */
export function reloadOnceForChunkError(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const key = CHUNK_RELOAD_KEY_PREFIX + window.location.pathname;
    const last = Number(window.sessionStorage.getItem(key) || 0);
    if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export interface ErrorReportMetadata {
  url?: string;
  userId?: string;
  [key: string]: any;
}

/**
 * Report an error to the API. Catches and silently fails - never throws.
 */
export async function reportError(
  error: Error | string,
  metadata?: ErrorReportMetadata
): Promise<void> {
  try {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;

    // Dedup
    const fp = fingerprint(message, stack);
    if (recentErrors.has(fp)) return;
    recentErrors.add(fp);
    cleanup();

    const body = {
      message: message.substring(0, 2000),
      stack: stack?.substring(0, 10000),
      source: 'frontend' as const,
      url: metadata?.url || (typeof window !== 'undefined' ? window.location.href : undefined),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      userId: metadata?.userId,
      metadata,
    };

    // Fire and forget - use sendBeacon if available for reliability during page unload
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      navigator.sendBeacon(ERROR_REPORT_ENDPOINT, blob);
    } else {
      fetch(ERROR_REPORT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => {});
    }
  } catch {
    // Never throw from error reporter
  }
}
